import DoctorStyle from './Doctors.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, IconButton, Tooltip, Typography } from '@mui/material';
import DeleteDialog from '../../components/DeleteDialog';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import DoctorDialog from './DoctorDialog';
import CommonAgGrid from '../../components/CommonAgGrid/CommonAgGrid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { deleteDoctor, getDoctors } from '../../apis/doctorSlice';
import moment from 'moment/moment';

export default function Doctors({ search }) {
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [operationMode, setOperationMode] = useState("Add");

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const { loggedIn } = useSelector((state) => state.authData);
  const { doctors, docLoading, totalCount } = useSelector(
    (state) => state.doctorData || { doctors: [], docLoading: false, totalCount: 0 },
  );

  async function callApi() {
    dispatch(getDoctors({ page, pageSize, search: search || "" }));
  }

  useEffect(() => {
    callApi();
  }, [page, pageSize, dispatch, loggedIn, search]);

  const handlePaginationModelChange = (model) => {
    setPage(model.page);
    setPageSize(model.pageSize);
  };

  const handleEdit = (data) => {
    setOpen(true);
    setEditData(data)
    setOperationMode("Edit")
  }

  const handleDelete = async (data) => {
    const response = await dispatch(deleteDoctor(data._id));

    if (response?.payload?.success) {
      toast.success(response?.payload.message);
      callApi();
    } else {
      toast.error("Error deleting doctor");
    }
  }

  const handleDeleteClick = (data) => {
    setDeleteRow(data);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteRow) return;
    await handleDelete(deleteRow);
    setDeleteOpen(false);
    setDeleteRow(null);
  };

  const handleCancelDelete = () => {
    setDeleteOpen(false);
    setDeleteRow(null);
  };

  const columns = useMemo(
    () => [
      { field: 'clinicName', headerName: 'Clinic Name', minWidth: 140, flex: 1, valueGetter: (p) => p.data?.clinicId?.name || '-' },
      { field: 'name', headerName: 'Name', minWidth: 130, flex: 1 },
      { field: 'email', headerName: 'Email', minWidth: 160, flex: 1.2 },
      { field: 'phone', headerName: 'Phone', minWidth: 110, flex: 0.9 },
      {
        field: 'joiningDate',
        headerName: 'Joining Date',
        minWidth: 120,
        flex: 0.9,
        valueGetter: (p) => (p.data?.joiningDate ? moment(p.data.joiningDate).format('DD-MM-YYYY') : '-'),
      },
      {
        field: 'docSpeciality',
        headerName: 'Doctor Speciality',
        minWidth: 150,
        flex: 1,
        valueGetter: (p) => p.data?.docSpeciality?.name || '-',
      },
      {
        colId: 'action',
        headerName: 'Action',
        width: 96,
        flex: 0,
        minWidth: 96,
        maxWidth: 96,
        sortable: false,
        pinned: 'right',
        cellRenderer: (params) => (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Tooltip title="Edit">
              <IconButton onClick={() => handleEdit(params.data)} color="primary" size="small" aria-label="edit">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton onClick={() => handleDeleteClick(params.data)} color="error" size="small" aria-label="delete">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [],
  );

  return (
    <div className={DoctorStyle.mainDataTable}>
      <DeleteDialog
        open={deleteOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        compact
      />
      <Box className={DoctorStyle.pageHead}>
        <Typography className={DoctorStyle.pageTitle}>Doctors</Typography>
      </Box>
      <Card className={DoctorStyle.tableCard}>
        <div className={DoctorStyle.tableHeader}>
          <h2 className={DoctorStyle.tableTitle}>Doctor List</h2>
          <Button
            className={DoctorStyle.addBtn}
            variant="contained"
            size="small"
            startIcon={<HealthAndSafetyIcon />}
            onClick={() => { setOpen(true); setOperationMode("Add") }}
          >
            Add Doctors
          </Button>
        </div>
        <CommonAgGrid
          rowData={doctors || []}
          columnDefs={columns}
          getRowId={(p) => String(p.data._id)}
          loading={docLoading}
          height={280}
          pagination
          paginationMode="server"
          rowCount={Number(totalCount) || 0}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[5, 10, 25]}
        />
        <DoctorDialog
          open={open}
          setOpen={setOpen}
          editData={editData}
          operationMode={operationMode}
          setOperationMode={setOperationMode}
          callApi={callApi}
        />
      </Card>
    </div>
  );
}
